import type { Sound } from './supabase-types'
import { TESLA_FACTS } from './tesla-facts'

/**
 * The duration ceiling comes from lib/tesla-facts.ts, per docs/ANTI_SLOP.md:
 * a spec never gets restated locally. This file used to hardcode 2 and tell
 * the user "Tesla allows up to 2s", while the trimmer, the voice generator
 * and lib/audio-utils all used 5. The site contradicted itself, and both
 * numbers were framed as a Tesla limit that tesla-facts.ts explicitly does
 * not claim: the duration guidance is RECOMMENDED, not a published spec.
 */
const TESLA_MAX_DURATION = TESLA_FACTS.audio.recommended.maxDurationSeconds
const QUIET_VOLUME_THRESHOLD = 60

/**
 * The one notice a long sound gets on its detail page. The unit is spelled out
 * to match TESLA_FACTS ("1–5 seconds") and to stay clear of the banned "3.4s".
 */
export function getDurationHint(sound: Sound): string | null {
  if (!sound.duration || sound.duration <= TESLA_MAX_DURATION) return null
  return `This sound runs ${sound.duration.toFixed(1)} seconds. Tesla works best at ${TESLA_FACTS.audio.recommended.duration}.`
}

/**
 * What the Download button on a long sound will hand over: the default
 * 0 to 5 s window reads "first 5 seconds", a window the visitor picked reads
 * "selected 3.2 seconds".
 */
export function trimDownloadSpan(start: number, end: number): string {
  const length = Math.max(0, end - start)
  const isDefaultWindow = start === 0 && end === TESLA_MAX_DURATION
  const figure = Number.isInteger(length) ? String(length) : length.toFixed(1)
  const unit = figure === '1' ? 'second' : 'seconds'
  return `${isDefaultWindow ? 'first' : 'selected'} ${figure} ${unit}`
}

export function getVolumeHint(sound: Sound): string | null {
  if (sound.volume_db === undefined || sound.volume_db === null) return null
  if (sound.volume_db >= QUIET_VOLUME_THRESHOLD) return null
  return 'This sound is on the quieter side. Crank Tesla volume to 7+ for best effect.'
}

export function getNormalizationBadge(sound: Sound): 'normalized' | 'quiet' | null {
  if (sound.is_normalized) return 'normalized'
  if (sound.volume_db !== undefined && sound.volume_db !== null && sound.volume_db < QUIET_VOLUME_THRESHOLD) return 'quiet'
  return null
}

/**
 * Returns the highest-priority pre-step install hint for the accordion, or null.
 * Priority: volume > normalization.
 */
export function getInstallHint(sound: Sound): string | null {
  // A long sound is announced once, by getDurationHint beside the Download
  // button, so it is not repeated here.
  if (typeof sound.volume_db === 'number' && sound.volume_db < QUIET_VOLUME_THRESHOLD) {
    return 'This sound is on the quieter side. Crank Tesla volume to 7+ for best effect.'
  }
  if (sound.is_normalized === false) {
    return 'Volume not normalized. Preview before installing.'
  }
  return null
}
